
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Shield, CheckCircle, XCircle } from 'lucide-react';
import { ComplianceRule } from '@/types/compliance';

interface ComplianceRulesViewerProps {
  rules: ComplianceRule[];
}

const RuleItem: React.FC<ComplianceRule> = React.memo(({ id, title, description, status }) => {
  return (
    <div className="border rounded-md p-4">
      <div className="flex items-start gap-3">
        {status === 'breached' ? (
          <div className="bg-red-100 text-red-600 p-1 rounded-full">
            <XCircle className="h-5 w-5" />
          </div>
        ) : (
          <div className="bg-green-100 text-green-600 p-1 rounded-full">
            <CheckCircle className="h-5 w-5" />
          </div>
        )}
        <div>
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold mb-2 ${
            status === 'breached' 
              ? 'bg-red-100 text-red-800' 
              : 'bg-green-100 text-green-800'
          }`}>
            {status === 'breached' ? 'Breached' : 'Compliant'}
          </div>
          <h3 className="font-medium">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
});

RuleItem.displayName = 'RuleItem';

const ComplianceRulesViewer: React.FC<ComplianceRulesViewerProps> = React.memo(({ rules }) => {
  // Sort rules to show breached ones first
  const sortedRules = React.useMemo(() => 
    [...rules].sort((a, b) => {
      if (a.status === 'breached' && b.status !== 'breached') return -1;
      if (a.status !== 'breached' && b.status === 'breached') return 1;
      return 0;
    })
  , [rules]);

  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Compliance Rules
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ScrollArea className="h-[600px] pr-4">
          <div className="space-y-6">
            {sortedRules.map((rule) => (
              <RuleItem key={rule.id} {...rule} />
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
});

ComplianceRulesViewer.displayName = 'ComplianceRulesViewer';

export default ComplianceRulesViewer;
